import flatpickr from "flatpickr";
import { Instance } from "flatpickr/dist/types/instance";
import { Dom } from "../../../common/dom";
import { Helper } from "../../../common/helper";
import { Txt } from "../../../common/text";
import { EditControl } from "../../form-row";
import { DatePickerHelper } from "./date-picker-helper";

export class DateRangeControl implements EditControl
{
    private readonly input: HTMLInputElement;
    private readonly inputRow: HTMLDivElement;
    private fp: Instance;
    private onChangeAction = () =>
    {};
    private currentValue: string;

    constructor(placeholder?: string)
    {
        this.inputRow = Dom.createDiv();
        const inputGroup = Dom.addDiv(this.inputRow, "w-input-group", "date", "w-date-picker-group");
        this.input = Dom.add("input", inputGroup, "w-form-control", "w-date-picker-input");
        this.input.placeholder = placeholder || "";
        this.input.type = "text";

        this.fp = flatpickr(this.input, {
            ...DatePickerHelper.baseOptions(),
            mode: "range",
            // Two months side by side on desktop; a single month with prev/next
            // navigation on mobile, since two months don't fit a phone screen.
            showMonths: Helper.isMobile() ? 1 : 2,
            onReady: (_dates, _str, instance) => this.buildRanges(instance),
            onChange: (selectedDates) =>
            {
                if (selectedDates.length === 2)
                {
                    const value = DatePickerHelper.format(selectedDates[0])
                        + "|" + DatePickerHelper.format(selectedDates[1]);
                    if (this.currentValue !== value)
                    {
                        this.currentValue = value;
                        this.notifyChange();
                    }
                }
            }
        });
        this.input.closest(".flatpickr-wrapper")?.classList.add("w-date-picker-wrapper");

        this.input.addEventListener("blur", () =>
        {
            if (!this.input.value)
            {
                this.currentValue = null;
                this.notifyChange();
            }
        });
    }

    private get ranges(): { label: string; start: Date; end: Date }[]
    {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const date = now.getDate();
        const quarterStartMonth = Math.floor(month / 3) * 3;

        return [
            { label: Txt.rangeYesterday, start: new Date(year, month, date - 1), end: new Date(year, month, date - 1) },
            { label: Txt.rangeToday, start: new Date(year, month, date), end: new Date(year, month, date) },
            { label: Txt.rangeLast7days, start: new Date(year, month, date - 6), end: new Date(year, month, date) },
            { label: Txt.rangeNext7days, start: new Date(year, month, date), end: new Date(year, month, date + 6) },
            { label: Txt.rangeLastMonth, start: new Date(year, month - 1, 1), end: new Date(year, month, 0) },
            { label: Txt.rangeCurrentMonth, start: new Date(year, month, 1), end: new Date(year, month + 1, 0) },
            { label: Txt.rangeNextMonth, start: new Date(year, month + 1, 1), end: new Date(year, month + 2, 0) },
            { label: Txt.rangePreviousQuarter, start: new Date(year, quarterStartMonth - 3, 1), end: new Date(year, quarterStartMonth, 0) },
            { label: Txt.rangeCurrentQuarter, start: new Date(year, quarterStartMonth, 1), end: new Date(year, quarterStartMonth + 3, 0) }
        ];
    }

    /** Adds the preset-range sidebar. */
    private buildRanges(instance: Instance)
    {
        const container = Dom.createDiv("flatpickr-ranges");
        const list = Dom.add("ul", container);

        this.ranges.forEach(range =>
        {
            const item = Dom.add("li", list);
            item.textContent = range.label;
            item.addEventListener("click", () =>
            {
                instance.setDate([range.start, range.end], true);
                instance.close();
            });
        });

        instance.calendarContainer.classList.add("w-date-picker", "hasRanges");
        instance.calendarContainer.appendChild(container);
    }

    initialize(parent: HTMLElement)
    {
        parent.append(this.inputRow);
    }

    async setValue(value: string)
    {
        this.currentValue = value;
        if (value)
        {
            const parts = value.split("|");
            this.fp.setDate([parts[0], parts[1]], false);
        }
        else
        {
            this.fp.clear();
        }
    }

    getValue()
    {
        return this.currentValue;
    }

    onChange(action: () => void)
    {
        this.onChangeAction = action;
    }

    focus()
    {
        this.input.focus();
    }

    private notifyChange()
    {
        if (this.onChangeAction)
        {
            this.onChangeAction();
        }
    }
}
